import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  admDashboardQuery,
  type AdmCi,
  type AdmCounters,
  type AdmCredential,
  type AdmPendingRow,
  type AdmRun,
} from '../../api/admDashboard'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { AdmFilterBar, AdmField, WindowPicker } from './AdmFilterBar'
import { AdmViews } from './AdmViews'
import { ADM_REFRESH_MS, useAutoRefresh } from './useAutoRefresh'

/**
 * ADM -> Discovery, din `views/version-3/softsee/adm/dashboard/`.
 *
 * Cartonasele, cele patru panouri si bara de filtre, cu markupul si clasele
 * portalului (`dashboard-stat`, `adm-section`, `adm-card`, `adm-list`), ca
 * `public/css/adm.css` sa deseneze la fel.
 *
 * CIFRELE NU MAI SUNT ANIMATE. In portal, `$.animateNumber()` le urca de la
 * zero, si de-aia valoarea statea in `data-number` iar celula pornea de la 0 -
 * asta cerea jQuery si pluginul lui. Numarul se scrie direct: o cifra care urca
 * o secunda e o cifra pe care n-o poti citi o secunda, si pe un ecran unde
 * lumea vine sa vada daca are ceva de facut, asta e chiar secunda care conteaza.
 */

const STYLES = ['css/adm.css']

/** Bifa de reimprospatare, tinuta pe statia omului (nu e o setare a firmei). */
const LIVE_KEY = 'adm.dashboard.autoRefresh'

function readLive(): boolean {
  try {
    return window.localStorage.getItem(LIVE_KEY) !== '0'
  } catch {
    return true
  }
}

function writeLive(on: boolean): void {
  try {
    window.localStorage.setItem(LIVE_KEY, on ? '1' : '0')
  } catch {
    /* fara stocare locala bifa tine doar cat sta pagina deschisa */
  }
}

/** Un cartonas din randul de sus - `dashboard-stat` din machetele platformei. */
function Tile({
  tone,
  icon,
  value,
  desc,
  href,
}: {
  tone: string
  icon: string
  value: number | string
  desc: string
  href: string
}) {
  return (
    <div className="col-md-3 col-sm-6">
      <div className={`dashboard-stat ${tone}`}>
        <div className="visual">
          <i className={icon}></i>
        </div>
        <div className="details">
          <div className="number">{value}</div>
          <div className="desc">{desc}</div>
        </div>
        <a className="more" href={href}>
          View more <i className="fa fa-arrow-circle-right"></i>
        </a>
      </div>
    </div>
  )
}

/**
 * Cele doua procente din cerinta 73, fiecare langa tinta lui.
 *
 * Un singur cartonas, nu doua: intrebarea e una - "suntem unde trebuie?" - si
 * se citeste pe amandoua deodata. Verde cand procentul a atins tinta, galben
 * cand nu; cifra neagra n-ar spune care din doua e cea scurta.
 */
export function CoverageTargets({ counters, query }: { counters: AdmCounters; query: string }) {
  const lines = [
    { id: 'coverage', label: 'Inventory discovered', value: counters.coveragePct, target: counters.coverageTarget },
    { id: 'correlation', label: 'Correlated', value: counters.correlationPct, target: counters.correlationTarget },
  ]

  return (
    <div id="admCoverageTargets">
      <div className="adm-section">
        <h4>
          <i className="fas fa-bullseye"></i> Coverage against targets
        </h4>
        <span className="adm-section-note">
          {counters.coverageDay ? `as of ${counters.coverageDay}` : 'the daily summary has not run yet'}
        </span>
        <a className="adm-section-link" href={`/app/adm/coverage${query}`}>
          Per customer <i className="fa fa-arrow-circle-right"></i>
        </a>
      </div>
      <div>
        {lines.map((line) => {
          const met = line.value >= line.target
          return (
            <div key={line.id} className="adm-coverage-line" data-line={line.id} data-met={met ? '1' : '0'} style={{ marginBottom: 8 }}>
              <div>
                <strong>{line.label}</strong>{' '}
                <span className="adm-coverage-value">{line.value}%</span>{' '}
                <span className="adm-muted">/ target {line.target}%</span>{' '}
                <span className={`label ${met ? 'label-success' : 'label-warning'}`}>{met ? 'on target' : 'below target'}</span>
              </div>
              <div className={`adm-progress${met ? '' : ' adm-progress-warn'}`}>
                <span style={{ width: `${Math.min(100, line.value)}%` }}></span>
                <em>
                  {line.value}% / {line.target}%
                </em>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function Tiles({ counters, query }: { counters: AdmCounters; query: string }) {
  return (
    /* Sase de-a latul. Cele doua procente nu mai sunt cartonase aici: stau in
       cartonasul de acoperire de dedesubt, langa tintele lor. */
    <div className="row adm-tiles-6">
      <Tile tone="blue-madison" icon="fas fa-satellite-dish" value={counters.runsActive} desc="Scans running" href={`/app/adm/runs${query}`} />
      <Tile tone="brown" icon="fa fa-cubes" value={counters.ciTotal} desc="Configuration items" href="/app/adm/map" />
      <Tile tone="green-mode" icon="fa fa-plus-circle" value={counters.ciNew} desc={`New in the last ${counters.windowDays} day(s)`} href={`/app/adm/runs${query}`} />
      <Tile tone="red-intense" icon="fas fa-exclamation-triangle" value={counters.errorsActive} desc="Unresolved failures" href={`/app/adm/errors${query}`} />
      <Tile tone="blue-mauve" icon="fa fa-clock" value={counters.ciStale} desc={`Not seen for ${counters.staleDays} days`} href={`/app/cmdb/health${query}`} />
      <Tile
        tone={counters.credentialsFailed > 0 ? 'red-intense' : 'brown'}
        icon="fa fa-key"
        value={counters.credentialsFailed}
        desc="Credentials failing (24h)"
        href="/app/settings/softsee#credentials"
      />
    </div>
  )
}

function ActiveRuns({ runs }: { runs: AdmRun[] }) {
  if (runs.length === 0) {
    return (
      <div className="adm-empty-inline">
        <i className="fas fa-satellite-dish"></i> No scan is running at the moment.
      </div>
    )
  }

  return (
    <table className="table adm-list" id="admActiveRunsTable" style={{ width: '100%' }}>
      <thead>
        <tr>
          <th>Customer</th>
          <th>Relay</th>
          <th>Started</th>
          <th style={{ width: 180 }}>Progress</th>
          <th className="adm-numeric">New</th>
          <th className="adm-numeric">Already known</th>
          <th className="adm-numeric">Failures</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => (
          <tr key={run.id}>
            <td>{run.companyName}</td>
            <td className="adm-muted">{run.relayName}</td>
            <td className="adm-nowrap adm-muted">{run.startedAt}</td>
            <td>
              {run.percent === null ? (
                /* O scanare fara numar de tinte e chiar la inceput si n-are ce
                   raporta. Zero acolo s-ar citi "blocata", ceea ce e altceva. */
                <span className="adm-muted">preparing…</span>
              ) : (
                <div className="adm-progress">
                  <span style={{ width: `${run.percent}%` }}></span>
                  <em>
                    {run.targetsResponded} / {run.targetsTotal}
                  </em>
                </div>
              )}
            </td>
            <td className="adm-numeric">{run.ciCreated}</td>
            <td className="adm-numeric">{run.ciUpdated}</td>
            <td className={run.errorsCount > 0 ? 'adm-numeric text-danger' : 'adm-numeric adm-muted'}>{run.errorsCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function RecentCis({ items }: { items: AdmCi[] }) {
  if (items.length === 0) {
    return (
      <div className="adm-empty-inline">
        <i className="fa fa-cubes"></i> Nothing has been discovered yet.
      </div>
    )
  }

  return (
    <table className="table adm-list">
      <tbody>
        {items.map((item) => (
          <tr key={item.id}>
            <td>
              <a href={`/app/adm/impact/${item.id}`}>{item.name}</a>
              <div className="adm-muted" style={{ fontSize: 11 }}>
                {item.className}
                {item.companyName ? ` · ${item.companyName}` : ''}
              </div>
            </td>
            <td className="adm-nowrap text-right">
              <span className={`adm-imp adm-imp-${item.criticality || 0}`}>{item.criticalityLabel}</span>
              <div className="adm-muted" style={{ fontSize: 11 }}>
                {item.firstSeen}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Waiting({ items, total }: { items: AdmPendingRow[]; total: number }) {
  if (items.length === 0) {
    return (
      <div className="adm-empty-inline">
        <i className="fa fa-check-circle"></i> Nothing is waiting for a decision.
      </div>
    )
  }

  return (
    <>
      <table className="table adm-list" id="admPendingPreview">
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                {item.deviceName || item.ip}
                <div className="adm-muted" style={{ fontSize: 11 }}>
                  {item.ip}
                  {item.companyName ? ` · ${item.companyName}` : ''}
                  {item.matchedName ? ` · looks like ${item.matchedName}` : ''}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > items.length ? (
        <div className="adm-muted" style={{ fontSize: 12, padding: '4px 8px' }}>
          {total - items.length} more in the <a href="/app/adm/pending">full queue</a>.
        </div>
      ) : null}
    </>
  )
}

function Credentials({ items, total }: { items: AdmCredential[]; total: number }) {
  if (items.length === 0) {
    return (
      <div className="adm-empty-inline">
        <i className="fa fa-key"></i> No credentials in the vault yet.
      </div>
    )
  }

  return (
    <>
      <table className="table adm-list">
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                {item.name}
                <div className="adm-muted" style={{ fontSize: 11 }}>
                  {item.typeLabel}
                  {item.username ? ` · ${item.username}` : ''}
                </div>
              </td>
              <td className="adm-nowrap text-right">
                {/* Starea si ultima reusita se citesc IMPREUNA: una care a mers
                    saptamana trecuta si a cazut de patruzeci de ori de atunci e
                    alta problema decat una care n-a mers niciodata. */}
                {item.state === 'expired' ? (
                  <span className="adm-pill adm-pill-bad">Expired</span>
                ) : item.state === 'failing' ? (
                  <span className="adm-pill adm-pill-bad">{item.failures24h} failures</span>
                ) : item.state === 'working' ? (
                  <span className="adm-pill adm-pill-ok">Working</span>
                ) : (
                  <span className="adm-pill">Never tested</span>
                )}
                <div className="adm-muted" style={{ fontSize: 11 }}>
                  {item.lastOk ? `ok ${item.lastOk}` : ' '}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > items.length ? (
        <div className="adm-muted" style={{ fontSize: 12, padding: '4px 8px' }}>
          {total - items.length} more in the <a href="/app/settings/softsee#credentials">vault</a>.
        </div>
      ) : null}
    </>
  )
}

export function DiscoveryDashboardPage() {
  const shell = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shell.data?.version ?? '')

  const [days, setDays] = useState(30)
  const [relay, setRelay] = useState(0)

  const page = useQuery({ ...admDashboardQuery(days, relay), placeholderData: keepPreviousData })
  const data = page.data

  /* Cerinta 91: cifrele si scanarile in curs se reimprospateaza singure la 30 s,
     ca in portal (`adm.js`) - dar nu cat tab-ul e ascuns si fara cereri
     suprapuse (vezi `useAutoRefresh`). `cancelRefetch: false`: o cerere deja
     plecata (o schimbare de filtru) nu se taie, bataia se alipeste ei. */
  const [live, setLive] = useState(readLive)
  useAutoRefresh(live, () => page.refetch({ cancelRefetch: false }))
  const updatedAt = page.dataUpdatedAt > 0 ? new Date(page.dataUpdatedAt).toLocaleTimeString('en-GB') : ''

  /* Filtrele pleaca mai departe in legaturile de coborare, ca operatorul sa nu
     piarda ce se uita - `Adm::filterQuery()`. */
  const query = `?days=${days}${relay > 0 ? `&relay_device_id=${relay}` : ''}`

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="blue-hoki"
          icon="fa-satellite-dish"
          title="ADM Discovery"
          note="what the scans have found on the network, and whether they are keeping up"
        />

        <AdmViews views={data?.views ?? []} current="dashboard" />

        <AdmFilterBar>
          {(data?.relays.length ?? 0) > 0 ? (
            <AdmField label="Relay" htmlFor="admRelay" wide>
              <select className="form-control input-sm" id="admRelay" name="relay_device_id" value={relay} onChange={(e) => setRelay(Number(e.target.value))}>
                <option value={0}>Any relay</option>
                {(data?.relays ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.companyName ? ` — ${r.companyName}` : ''}
                  </option>
                ))}
              </select>
            </AdmField>
          ) : null}
          <WindowPicker windows={data?.windows ?? [1, 7, 30, 90]} value={days} onChange={setDays} />
          <div className="adm-field" id="admAutoRefresh">
            <label htmlFor="admLive">Refresh</label>
            <div className="checkbox" style={{ margin: 0 }}>
              <label>
                <input
                  type="checkbox"
                  id="admLive"
                  checked={live}
                  onChange={(e) => {
                    setLive(e.target.checked)
                    writeLive(e.target.checked)
                  }}
                />{' '}
                every {ADM_REFRESH_MS / 1000} s
              </label>
              {updatedAt !== '' ? (
                <span className="adm-muted" id="admUpdatedAt" style={{ fontSize: 11, marginLeft: 6 }}>
                  updated {updatedAt}
                  {page.isFetching ? ' …' : ''}
                </span>
              ) : null}
            </div>
          </div>
        </AdmFilterBar>

        {page.isError ? (
          <div className="adm-empty-inline">
            <i className="fas fa-exclamation-triangle"></i> {(page.error as Error).message}
          </div>
        ) : null}

        <div className="adm-tiles" id="admCounters">
          {data ? <Tiles counters={data.counters} query={query} /> : null}
        </div>

        {data && data.showCoverage ? <CoverageTargets counters={data.counters} query={query} /> : null}

        <div className="adm-section adm-section-blue-hoki">
          <h4>
            <i className="fas fa-satellite-dish"></i> Running now
          </h4>
          <span className="adm-section-note">scans in progress</span>
        </div>
        <div id="admActiveRuns">{data ? <ActiveRuns runs={data.activeRuns} /> : null}</div>

        <div className="adm-section adm-section-yellow">
          <h4>
            <i className="fas fa-hand-paper"></i> Needs a decision
          </h4>
          <span className="adm-section-note">nothing here moves until somebody looks at it</span>
        </div>

        <div className="row">
          <div className="col-md-4">
            <div className="adm-card adm-tone-yellow">
              <div className="adm-card-head">
                <h5>
                  <i className="fas fa-cube"></i> Recently discovered
                </h5>
              </div>
              <div id="admRecentCis">{data ? <RecentCis items={data.recentCis} /> : null}</div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="adm-card adm-tone-yellow">
              <div className="adm-card-head">
                <h5>
                  <i className="fas fa-inbox"></i> Waiting for review
                </h5>
                <a className="adm-card-link" href="/app/adm/pending">
                  Queue
                </a>
              </div>
              <div id="admUnclassified">{data ? <Waiting items={data.pending} total={data.pendingTotal} /> : null}</div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="adm-card adm-tone-yellow">
              <div className="adm-card-head">
                <h5>
                  <i className="fas fa-key"></i> Credential health
                </h5>
                {/* Setarile Softsee, nu Inventoree: fila seifului sta in
                    `softsee_settings/tabList`, iar ancora inseamna ceva numai
                    pe pagina care tine fila. */}
                <a className="adm-card-link" href="/app/settings/softsee#credentials">
                  Vault
                </a>
              </div>
              <div id="admCredentials">{data ? <Credentials items={data.credentials} total={data.credentialsTotal} /> : null}</div>
            </div>
          </div>
        </div>
      </div>
    </Content>
  )
}
